/**
 * 审核管线的通用契约 —— **服务端和前端共用**。
 *
 * ---------------------------------------------------------------------------
 * 为什么要做成通用的（而不是"先做资源库的，M4 再说"）
 * ---------------------------------------------------------------------------
 * M4 要加日历、视频、种子三类内容，它们**全都要走同一条管线**：
 * 提交 → 待审 → 发布 → 留痕。
 *
 * 如果第一版把资源库的字段（kind / url / tags）写死在审核界面里，
 * M4 加第二类内容时就只有两条路：
 *   · 复制一份界面改字段（三份几乎一样的代码，改一处漏两处）
 *   · 回头重构（那时已经有真实审核数据在跑，改动风险大得多）
 * 两条都难。所以**第一版就把它抽象出来** —— 代价是现在多写一点类型，
 * 收益是 M4 只需要注册一条新类型。
 *
 * ---------------------------------------------------------------------------
 * 抽象的形状：队列项是通用的，审核内容是各类型自己的
 * ---------------------------------------------------------------------------
 * 分成两层，因为这两件事的"通用程度"不同：
 *
 *   1. **队列项**（下面 QueueItem）—— 完全通用。
 *      审核员在列表里需要的信息，任何内容类型都一样：
 *      标题、作者、是不是对已发布内容的修改、什么时候提交的。
 *
 *   2. **审核详情**（各类型自己的 payload）—— 不通用。
 *      审核资源库要看 URL 和正文，审核种子要看种子值和结构，
 *      审核日历要看时间和地点。硬要统一成一个字段表，就会得到
 *      一堆"类型 A 用、类型 B 不用"的可空字段 —— 那比不抽象更糟。
 *
 * 所以：**列表通用，详情按类型**。前端用一个注册表把两者接起来。
 */

// ---------------------------------------------------------------------------
// 可审核的内容类型
// ---------------------------------------------------------------------------

/**
 * 已经接入审核管线的内容类型。
 *
 * ⚠️ 加新类型时要同时改三处，缺一处就会出现"队列里有但点不开"或反过来：
 *   1. 这里加键
 *   2. 服务端 `services/api/src/review/registry.ts` 注册实现
 *   3. 前端 `apps/web/src/lib/reviewTypes.tsx` 注册界面
 *     —— 三个地方的类型都从这里推导，所以少改一处**编译就会报错**，
 *        不会静默漏掉。这是刻意把类型放 shared 的原因。
 *
 * ⚠️ 三处必须**在同一个改动里**完成，不能分两次提交。
 *    后两处是穷尽的 `Record<ReviewableType, …>`，只加这里会让服务端和前端
 *    同时编译不过 —— 那看起来像"改坏了"，其实是设计如此（宁可编译失败，
 *    也不要一个队列里有、点开却没有任何实现的类型）。
 */
/*
 * ⚠️ 只列**已经实现**的类型。
 *
 * 提前把还没做的类型写进来是错的：所有通用代码都会以为它们存在
 * （队列会去查、类型检查会通过），而运行时要么报错要么静默返回空 ——
 * 那正是"看起来做完了其实没有"。加类型时这三个地方都要动，少一个就编译不过：
 *   1. 这里
 *   2. services/api/src/review/registry.ts 的 IMPLEMENTATIONS
 *   3. apps/web/src/lib/reviewTypes.tsx 的注册表
 *
 * ---------------------------------------------------------------------------
 * 为什么 M4 只加了 `calendar`，而 video / seed 不在这里
 * ---------------------------------------------------------------------------
 * 这两个不是漏了，是**有意不加**的：
 *
 *   · `video` —— Q6 决定「贴了就可见，**不审核**」。视频没有待审状态，
 *     所以它压根不该出现在这个名单里。没有 `video.review` 这个能力，
 *     权限矩阵、导航、队列标签上也都不该有它。
 *     事后手段是作者可删、有 `resource.moderate` 的人可隐藏（那是治理，不是审核）。
 *
 *   · `seed` —— `seed.review` 能力确实存在（见 shared/permissions.ts），
 *     但 Q7/Q8 决定 v1 的 `/seeds` 只做 **Ranked Seeds 上游检索**，
 *     社区 UGC 分享（RSG/SSG 那套）**先留空**。没有任何用户提交的种子内容，
 *     所以现在注册它只会得到一个恒空的队列标签。
 *     等社区分享真的做了，再把 `seed` 加到这里 —— 那时它有东西可审。
 */
export const REVIEWABLE_TYPES = ['resource', 'calendar'] as const;
export type ReviewableType = (typeof REVIEWABLE_TYPES)[number];

/** 队列项。**完全通用** —— 任何内容类型的审核列表都长这样。 */
export interface QueueItem {
  type: ReviewableType;
  /** 该类型内的条目 id */
  id: string;
  /** 待审的版本号（没有版本概念的类型恒为 1） */
  revision: number;
  title: string;
  /**
   * 一行副标题，由各类型自己决定放什么。
   * 资源库放域名、日历放时间、种子放种子值 —— 审核员扫列表时靠它区分。
   */
  subtitle: string | null;
  /**
   * true = 这是对**已发布**条目的修改（线上仍展示旧版）
   * false = 全新条目
   *
   * ⚠️ 审核界面必须把这两种情况区分开：审核新条目是"要不要收"，
   *    审核修改是"这处改动能不能接受" —— 心理预期完全不同，
   *    而后者还应该能对照旧版看改了什么。
   */
  isEdit: boolean;
  authorName: string | null;
  /**
   * 待审的这一版是不是**当前查看者**提交的。
   *
   * ⚠️ 它不是一个装饰性的标记：reviewer 不能批准自己提交的内容
   *    （见 shared/permissions.ts 的 canSelfReview），所以审核界面要靠它
   *    提前把"这一条你批不了"说清楚 —— 否则审核员点下去才被拒。
   */
  mine: boolean;
  createdAt: number;
  /**
   * 这条内容**所描述的那个时刻已经过去了**。
   *
   * 这一条是 Q4c 直接带来的：用户要求「**过去的事件仍然进审核队列**」
   * （而不是自动过期、不进队列）。于是队列里会真的出现"三个月前那场比赛"。
   *
   * ⚠️ 审核界面**必须**把它显著标出来。审核员批准一版内容时做的判断是
   *    "这东西该不该出现在站上"；而面对一个已经结束的活动，这个判断
   *    要么没法做，要么答案无关紧要 —— 他不知道自己在审一个历史条目时，
   *    会把注意力花在"时间是不是填错了"上面，而那恰恰是最不该花的地方。
   *
   * 没有"过期"这个概念的类型（资源库）恒为 `false`。
   */
  expired: boolean;
}

export const QueueResponseSchemaShape = {
  items: [] as QueueItem[],
  total: 0,
};

export interface QueueResponse {
  items: QueueItem[];
  total: number;
  /** 各类型的待审数量，用于队列页的分类型筛选标签 */
  counts: Partial<Record<ReviewableType, number>>;
}

/**
 * 审核详情。
 *
 * `payload` 的形状由类型决定（见前端注册表的 renderDetail）。
 * 这里用 unknown 而不是 any —— 强制调用方在做类型判断之后再取值。
 */
export interface ReviewDetail {
  item: QueueItem;
  /**
   * 当前的线上版本（如果存在）。
   *
   * ⚠️ 有这个字段，"修改前后对比"才是可能的。审核员面对一次修改时
   *    最需要知道的就是"改了哪里"，而只给新内容的话他只能凭记忆比对。
   */
  live: Record<string, unknown> | null;
  /** 待审的内容 */
  pending: Record<string, unknown>;
  /**
   * 提交者附给审核员的一句话。可选，**不属于内容**。
   *
   * 它单独占一个字段而不是塞进 `pending` 里，是因为审核界面必须把它
   * 显示成"这个人对你说的话"，而不是内容的一部分 ——
   * 混在内容里的话，审核员既可能把它当成要上线的正文，
   * 也可能在"改动对比"里把它当成一处内容改动。
   */
  note: string | null;
  /** 该作者之前有没有被驳回过（审核时的重要参考） */
  previousRejections: number;
}

/** 驳回理由的长度上限，和服务端校验保持一致。 */
export const REJECT_REASON_MAX = 500;

/** 各类型在界面上的展示信息。放这里是让 M4 加类型时有个明确的填空处。 */
export const REVIEW_TYPE_LABELS: Record<ReviewableType, string> = {
  resource: '资源库',
  calendar: '日历',
};
